import React, { useRef } from 'react';
import { X, Printer, Download, Loader, AlertTriangle } from 'lucide-react';
import Button from '../common/Button';
import BadgePreview from '../badges/BadgePreview';
import PrintableStickerPreview from '../stickers/PrintableStickerPreview';
import ThermalLabelPreview from '../stickers/ThermalLabelPreview';
import { useBadgeSettings } from '../../context/BadgeSettingsContext';
import { useToastContext } from '../../context/ToastContext';

const BadgePreviewModal = ({
  isOpen,
  onClose,
  previewUrl,
  attendeeName,
  attendeeData,
  template,
  showQR,
  showLogo,
  logoConfig = {},
  isLoading,
  error,
  onPrint,
  printMode = 'badge', // 'badge' or 'sticker'
  eventId,
}) => {
  const toast = useToastContext();
  
  // Get printer settings from context
  const {
    stickerSize,
    stickerWidth,
    stickerHeight,
    useCustomStickerSize,
    printerType,
    thermalLabelSize,
    qrPosition,
    stickerNameFontSize,
    stickerPositionFontSize,
    stickerOrgFontSize,
    stickerLineHeight,
    stickerLogoSize,
    stickerShowLogo,
    stickerAutoScaleFont,
    stickerNameFontWeight,
    stickerPositionFontWeight,
    stickerOrgFontWeight
  } = useBadgeSettings();
  const badgeRef = useRef(null);

  if (!isOpen) return null;

  const handleDownload = async () => {
    if (!badgeRef.current) {
      toast.error('Badge preview not available for download.');
      return;
    }

    try {
      // Wait for all images (including logos) to load
      const images = badgeRef.current.querySelectorAll('img');
      await Promise.all(Array.from(images).map(img => {
        return new Promise((resolve) => {
          if (img.complete) {
            resolve();
          } else {
            img.onload = resolve;
            img.onerror = resolve; // Continue even if image fails
          }
        });
      }));

      // Use html2canvas to convert the badge to an image
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(badgeRef.current, {
        backgroundColor: '#f8fafc',
        scale: 3, // Higher quality
        useCORS: true,
        allowTaint: true,
        foreignObjectRendering: true,
        logging: false,
        width: badgeRef.current.offsetWidth,
        height: badgeRef.current.offsetHeight,
      });

      // Convert to blob and download
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `badge-${attendeeName || 'preview'}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      }, 'image/png', 0.95);
    } catch (error) {
      console.error('Error generating download:', error);
      toast.error('Unable to generate download. Please use the Print function instead.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-60 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">
              {printMode === 'sticker' ? 'Sticker' : 'Badge'} Preview for {attendeeName}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 bg-gray-50 min-h-[500px] flex items-center justify-center">
            {isLoading && (
              <div className="text-center text-gray-500">
                <Loader className="h-12 w-12 mx-auto animate-spin mb-4" />
                <p>Generating Badge Preview...</p>
              </div>
            )}
            {error && (
              <div className="text-center text-red-600">
                <AlertTriangle className="h-12 w-12 mx-auto mb-4" />
                <p>Could not generate preview.</p>
                <p className="text-sm text-gray-500 mt-1">{error}</p>
              </div>
            )}
            {!isLoading && !error && attendeeData && (
              <div className="flex justify-center mb-6" ref={badgeRef}>
                {printMode === 'sticker' ? (
                  printerType === 'thermal' ? (
                    <ThermalLabelPreview
                      layout={template}
                      data={{
                        name: attendeeData.name,
                        position: attendeeData.title || attendeeData.profession,
                        organization: attendeeData.company,
                        attendeeId: attendeeData.attendeeId,
                        eventId: eventId
                      }}
                      showQR={showQR}
                      qrPosition={qrPosition}
                      labelSize={thermalLabelSize}
                      showRoll={true}
                      nameFontSize={stickerNameFontSize}
                      positionFontSize={stickerPositionFontSize}
                      orgFontSize={stickerOrgFontSize}
                      lineHeight={stickerLineHeight}
                      logoSize={stickerLogoSize}
                      showLogo={stickerShowLogo}
                      autoScaleFont={stickerAutoScaleFont}
                      nameFontWeight={stickerNameFontWeight}
                      positionFontWeight={stickerPositionFontWeight}
                      orgFontWeight={stickerOrgFontWeight}
                    />
                  ) : (
                    <PrintableStickerPreview
                      layout={template}
                      data={{
                        name: attendeeData.name,
                        position: attendeeData.title || attendeeData.profession,
                        organization: attendeeData.company,
                        attendeeId: attendeeData.attendeeId,
                        eventId: eventId
                      }}
                      showQR={showQR}
                      qrPosition={qrPosition}
                      size={stickerSize}
                      customWidth={useCustomStickerSize ? stickerWidth : null}
                      customHeight={useCustomStickerSize ? stickerHeight : null}
                      showPaper={true}
                      paperSize="letter"
                      nameFontSize={stickerNameFontSize}
                      positionFontSize={stickerPositionFontSize}
                      orgFontSize={stickerOrgFontSize}
                      lineHeight={stickerLineHeight}
                      logoSize={stickerLogoSize}
                      showLogo={stickerShowLogo}
                      autoScaleFont={stickerAutoScaleFont}
                      nameFontWeight={stickerNameFontWeight}
                      positionFontWeight={stickerPositionFontWeight}
                      orgFontWeight={stickerOrgFontWeight}
                    />
                  )
                ) : (
                  <BadgePreview
                    layout={template}
                    data={attendeeData}
                    showQR={showQR}
                    showLogo={showLogo}
                    logoConfig={logoConfig}
                  />
                )}
              </div>
            )}
            {!isLoading && !error && previewUrl && !attendeeData && (
              <img
                src={previewUrl}
                alt={`Badge for ${attendeeName}`}
                className="max-w-full max-h-[500px] object-contain shadow-lg rounded-md"
              />
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 p-4 border-t bg-white">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              className="flex-1"
            >
              Close
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={handleDownload}
              className="flex-1 gap-2"
              disabled={isLoading || (!attendeeData && !previewUrl) || error}
            >
              <Download className="h-5 w-5" />
              Download
            </Button>
            <Button
              type="button"
              onClick={onPrint}
              className="flex-1 gap-2"
              disabled={isLoading || (!attendeeData && !previewUrl) || error}
            >
              <Printer className="h-5 w-5" />
              Print
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BadgePreviewModal;
